// React core imports
import React, { useState, useEffect, useRef } from "react";

// React Router imports
import { Link, useLocation } from "react-router-dom";

// Redux imports
import { useDispatch, useSelector } from "react-redux";

// Custom hooks and functions
import useScapePressed from "../../functions/useScapePressed";
import focusOnPupop from "../../functions/focusOnPopup";

// React Bootstrap components
import { Button, Container, Nav, Navbar, Offcanvas } from "react-bootstrap";

// Image assets
import layout from "../../assets/img/login.png";
import googlePlay from "../../assets/img/android.svg";
import appStore from "../../assets/img/iphone.svg";

// Event Bus for cross-component communication
import { eventBus } from "../../eventBus";

// Component-specific imports
import CustomerLogin from "./customerLogin";
import AgentLogin from "./agentsLogin";

// Stylesheets
import "./Header.css";
import "../../index.css";

const menuItems = [
  { label: "Home", icon: "home.svg", navigateUrl: "/uaichome" },
  { label: "About Us", icon: "aboutus.svg", navigateUrl: "/uaichome/aboutus" },
  {
    label: "Policy Holder",
    icon: "claims_portal.svg",
    //UNCOMMENT BELOW
    navigateUrl: "/uaichome/policyholder",
  },
  {
    label: "Claims Portal",
    icon: "claims_portal.svg",
    navigateUrl: "/uaichome/claims",
  },
  {
    label: "Become an Agent",
    icon: "become_an_agent.svg",
    navigateUrl: "/uaichome/FindAnAgent",
  },
  {
    label: "Insurance",
    icon: "insurance.svg",
    navigateUrl: "/uaichome/insurance",
  },
  {
    label: "Lien Holder",
    icon: "lien_holder.svg",
    navigateUrl: "/uaichome/leinholder",
  },
  { label: "Careers", icon: "careers.svg", navigateUrl: "/uaichome/careers" },
  { label: "FAQ's", icon: "faq.svg", navigateUrl: "/uaichome/claims/faq" },
  {
    label: "Contact",
    icon: "Contact_Icon.svg",
    navigateUrl: "/uaichome/contact",
  },
];

const Header = () => {
  // React Router hook to get current path
  const { pathname } = useLocation();

  // Modal visibility state and handlers
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  // Control flag to enable/disable continuation (e.g., form submission)
  const [canContinue, setCanContinue] = useState(false);

  // Reference to DOM element, used for focus or other direct manipulations
  const ref = useRef(null);

  // Responsive UI state
  const [responsiveStatus, setResponsiveStatus] = useState(false);

  // Redux dispatch function
  const dispatch = useDispatch();

  // Custom hook to detect Escape key press
  const scapePressed = useScapePressed();

  // Login display and tab states
  const [LoginDisplay, setLoginDisplay] = useState("AGENT");
  const [activeTab, setActiveTab] = useState("AGENT");

  // Radio button selection for customer login type
  const [selectedRadioButtonCustomer, setSelectedRadioButtonCustomer] =
    useState("insured_login");

  // Modal visibility state (another one? Consider unifying if possible)
  const [showModal, setShowModal] = useState(false);

  // Location-specific flag (e.g., Texas)
  const [texas, setTexas] = useState(false);

  // Agent login input states
  const [agentUsername, setAgentUserName] = useState("");
  const [agentPassword, setAgentPassword] = useState("");

  // Customer login input states
  const [customerUsername, setCustomerUsername] = useState("");
  const [customerPassword, setCustomerPassword] = useState("");

  // Form validation or input disabled states
  const [isAgentDisabled, setAgentDisabled] = useState(true);
  const [isCustomerDisabled, setCustomerDisabled] = useState(true);

  // Forgot password flags for each user type
  const [isForgotPasswordAgent, setIsForgotPasswordAgent] = useState(false);
  const [isForgotPasswordCustomer, setIsForgotPasswordCustomer] =
    useState(false);

  // Message content state (can hold HTML)
  const [weather_message_html, set_weather_message_html] =
    useState(`<p>Hello there</p>`);

  // User role flags
  const [isAgent, setIsAgent] = useState(true);
  const [isCustomer, setIsCustomer] = useState(false);

  // Redux selectors for UI menu states
  const isMobileMenuOpen = useSelector(
    (state) => state.isMobileMenuOpenReducer
  );
  const isAgentsLoginMenuOpen = useSelector(
    (state) => state.isAgentsLoginMenuOpenReducer
  );
  const isCustomerLoginMenuOpen = useSelector(
    (state) => state.isCustomerLoginMenuOpenReducer
  );
  const isLoginMenuOpen = useSelector((state) => state.isLoginMenuOpenReducer);
  const selectedLogintoDisplay = useSelector(
    (state) => state.selectedLogintoDisplayReducer
  );

  const showTexasAgentMessageReducer = useSelector(
    (state) => state.showTexasAgentMessageReducer
  );
  const texasAgentMessageReducer = useSelector(
    (state) => state.texasAgentMessageReducer
  );

  // Message display states
  const [isMessage, setIsMessage] = useState(false);
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  const [message, setMessage] = useState("");
  const [isconfirmLoginMessage, setIsConfirmLoginMessage] = useState(false);

  // Escape key handler to close modal and reset messages
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setShowModal(false);
        // set_weather_message_html(`<p>Hello there</p>`);
        setIsConfirmLoginMessage(false);
        // Additional logic for modal close if needed
      }
    };

    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  useEffect(() => {
    if (!isLoginMenuOpen) {
      setShowModal(false);
    }
  }, [isLoginMenuOpen]);

  // Clear message and password fields after 5 seconds when message changes
  useEffect(() => {
    if (message !== "") {
      const timer = setTimeout(() => {
        setMessage("");
        setCustomerPassword("");
        setAgentPassword("");
        // Reset only the password fields
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  // Update active tab based on selected login display value
  useEffect(() => {
    if (selectedLogintoDisplay === "CUSTOMER") {
      setActiveTab("CUSTOMER");
    } else if (selectedLogintoDisplay === "AGENT") {
      setActiveTab("AGENT");
    }
  }, [selectedLogintoDisplay]);

  // Subscribe to eventBus events for opening modal and switching to customer tab
  useEffect(() => {
    eventBus.subscribe("openModal", () => setShowModal(true));
    eventBus.subscribe("customerModel", () => setActiveTab("CUSTOMER"));

    // Consider adding unsubscribe logic on cleanup if supported by eventBus
  }, []);

  // Responsive status update based on media queries
  useEffect(() => {
    const mediaQueries = {
      isSmallMobile: window.matchMedia("(max-width: 400px)"),
      isMobile: window.matchMedia("(max-width: 768px)"),
      isTablet: window.matchMedia("(max-width: 900px)"),
      isTabletXL: window.matchMedia("(max-width: 1310px)"),
    };

    const updateItemsPerPage = () => {
      if (
        mediaQueries.isSmallMobile.matches ||
        mediaQueries.isMobile.matches ||
        mediaQueries.isTablet.matches ||
        mediaQueries.isTabletXL.matches
      ) {
        setResponsiveStatus(true);
      } else {
        setResponsiveStatus(false);
      }
    };

    // Initial check
    updateItemsPerPage();

    // Add listeners for media query changes
    Object.values(mediaQueries).forEach((mq) =>
      mq.addEventListener("change", updateItemsPerPage)
    );

    // Cleanup listeners on unmount
    return () => {
      Object.values(mediaQueries).forEach((mq) =>
        mq.removeEventListener("change", updateItemsPerPage)
      );
    };
  }, []);

  // Toggle body scroll lock based on responsive status
  useEffect(() => {
    if (responsiveStatus) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [responsiveStatus]);

  // Toggle body scroll lock based on modal visibility and responsive status
  useEffect(() => {
    if (showModal && responsiveStatus) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    // Ensure scroll unlock on cleanup
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showModal, responsiveStatus]);

  // Reset login inputs and show modal-related Redux dispatch on modal open/close
  useEffect(() => {
    if (!showModal) {
      setIsConfirmLoginMessage(false);
      // set_weather_message_html(`<p>Hello there</p>`);
      setAgentUserName("");
      setAgentPassword("");
      setCustomerUsername("");
      setCustomerPassword("");
      dispatch({
        type: "SET_LOGIN_PAGE_TO_DISPLAY",
        payload: "AGENT",
      });
      dispatch({
        type: "SET_TX_AGENT_DISPLAY",
        payload: false,
      });
      setMessage("");
      setActiveTab("AGENT");
    } else {
      dispatch({
        type: "OPEN_LOGIN_MENU",
      });
    }
  }, [showModal]);

  // Automatically hide error or success messages after 5 seconds
  useEffect(() => {
    if (isErrorMessage === true) {
      setTimeout(() => {
        setIsErrorMessage(false);
      }, 5000);
    } else if (isMessage === true) {
      setTimeout(() => {
        setIsMessage(false);
      }, 5000);
    }
  }, [isErrorMessage, isMessage]);

  // Close Pop up Modals when ESC button clicked
  useEffect(() => {
    if (isLoginMenuOpen) {
      closeLoginMenu();
      setIsConfirmLoginMessage(false);
    }
  }, [scapePressed]);

  // Auto Focus within the Modal that is opened
  useEffect(() => {
    if (isLoginMenuOpen) {
      focusOnPupop({ id: "login-popup-menu-container" });
    }
  }, [isLoginMenuOpen, selectedLogintoDisplay]);

  const errorMessageComponent = () => {
    return (
      <div
        className={
          isErrorMessage ? "error-message-shown" : "error-message-hidden"
        }
      >
        <div></div>
        <h3 style={{ color: "red" }}>{message}</h3>
      </div>
    );
  };
  const messageComponent = () => {
    return (
      <div className={isMessage ? "message-shown" : "message-hidden"}>
        <div></div>
        <h3 style={{ color: "red" }}>{message}</h3>
      </div>
    );
  };

  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    setCanContinue(el.scrollHeight - el.scrollTop - el.clientHeight < 5);
  };

  const closeLoginMenu = () => {
    // set_weather_message_html(`<p>Hello there</p>`);
    setIsConfirmLoginMessage(false);
    if (isLoginMenuOpen === true) {
      dispatch({
        type: "OPEN_LOGIN_MENU",
      });
    } else if (isAgentsLoginMenuOpen === true) {
      dispatch({
        type: "OPEN_AGENTS_LOGIN_MENU",
      });
    } else if (isCustomerLoginMenuOpen === true) {
      dispatch({
        type: "OPEN_CUSTOMER_LOGIN_MENU",
      });
    }
  };

  const toggleStyle = (type) => ({
    flex: 1,
    padding: "10px 20px",
    textAlign: "center",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight:
      type === "agent" && activeTab === "agent"
        ? 600
        : type === "customer" && activeTab === "customer"
        ? 600
        : "normal",
    color: activeTab === type ? "#0E72D6" : "#333",
    backgroundColor: "transparent",
    border: activeTab === type ? "1.3px solid #0E72D6" : "none",
    borderRadius: "6px",
    transition: "all 0.2s ease-in-out",
  });

  const confirmLoginMessage = () => {
    return (
      <div
        style={{
          padding: responsiveStatus ? ".5em" : "",
          width: responsiveStatus ? "100%" : "",
          marginBottom: responsiveStatus ? "1.5em" : "",
        }}
      >
        <div
          className={
            isconfirmLoginMessage
              ? "forgot-password-background-on"
              : "forgot-password-background-off"
          }
        ></div>
        <div
          className={
            isconfirmLoginMessage
              ? "confirm-login-message-component-open"
              : "confirm-login-message-component-closed"
          }
          style={{ position: "relative" }}
        >
          <div>
            <center>
              <h4>Please Read Before Continuing</h4>
            </center>
            <hr />
            <div
              ref={ref}
              className="weather-message"
              dangerouslySetInnerHTML={{ __html: texasAgentMessageReducer }}
              style={{
                overflowY: "scroll",
                height: 150,
                marginBottom: 16,
                color: "red",
              }}
              onScroll={onScroll}
            />
            <hr />
            <div className="continue-button-confirm-login">
              <button
                disabled={!canContinue}
                style={{
                  backgroundColor: " rgb(14, 114, 214)",
                  color: "white",
                  border: "none",
                  padding: "4px 6px",
                }}
                onClick={() => {
                  document.getElementById("mainFormAgent").submit();
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {!responsiveStatus && (
        <>
          <div
            className="d-flex flex-row justify-content-between"
            style={{
              background: "#1C1A1A",
              width: "100%",
            }}
          >
            <div
              style={{
                width:
                  responsiveStatus === "isDesktop"
                    ? "33%"
                    : responsiveStatus === "isTablet" ||
                      responsiveStatus === "isTabletLandscape"
                    ? "43%"
                    : "inherit",
                maxWidth: "450px",
                display: "flex",
                alignItems: "center", // vertically centers content
                background: "#0E72D6",
                color: "white",
                height: "48px",
                fontSize: "13px",
                paddingLeft: "30px", // optional: spacing from the left
                clipPath:
                  "polygon(0px 0px, 94% 0px, 100% 71%, 96% 100%, 0% 100%)",
              }}
            >
              Download our app on
              <div
                style={{
                  paddingLeft: "5px",
                  display: "flex",
                  alignItems: "center", // also ensures vertical alignment of icons
                }}
              >
                <a
                  target="_blank"
                  href="https://play.google.com/store/apps/details?id=com.phonegap.uaic&hl=en"
                  rel="noreferrer"
                  tabIndex={-1}
                >
                  <img
                    src={googlePlay}
                    className="social-media-icons"
                    alt="Google Play"
                    style={{
                      height: "30px",
                      width: "auto",
                      marginRight: "10px",
                    }}
                    tabIndex={0}
                  />
                </a>
                <a
                  target="_blank"
                  href="https://apps.apple.com/us/app/united-automobile-insurance/id868152074"
                  rel="noreferrer"
                >
                  <img
                    src={appStore}
                    alt="App Store"
                    className="social-media-icons"
                    style={{
                      height: "30px",
                      width: "auto",
                    }}
                  />
                </a>
              </div>
            </div>
            <div
              style={{
                color: "white",
                display: "flex",
                gap: "30px",
                alignItems: "center",
                fontSize: "13px",
                paddingRight: "30px",
              }}
            >
              {/* <span><img
                    src={require("../../assets/img/chats.svg")}
                    alt="United Logo"
                    className="img-fluid"
                    id="logo"
                    style={{
                      height: "22px",
                      width: "22px",
                      marginRight: "7px",
                      }}/> Let's chat</span> */}
              <span>
                <a
                  href="tel:305-940-5022"
                  style={{
                    color: "white",
                  }}
                  tabIndex={0}
                >
                  <img
                    src={require("../../assets/img/phone.svg")}
                    alt="United phone #"
                    className="img-fluid"
                    id="logo"
                    style={{
                      height: "20px",
                      width: "20px",
                      color: "white",
                    }}
                  />{" "}
                  (305)-940-5022
                </a>
              </span>
              <span>
                <Nav.Link
                  as={Link}
                  to="/uaichome/claims/faq"
                  active={pathname === "/uaichome/claims/faq"}
                >
                  <img
                    src={require("../../assets/img/questionmark.svg")}
                    alt="help-center-icon"
                    className="img-fluid"
                    id="logo"
                    style={{
                      height: "30px",
                      width: "30px",
                    }}
                  />{" "}
                  Help center
                </Nav.Link>
              </span>
            </div>
          </div>

          <Navbar expand="lg" style={{ paddingLeft: "0px" }}>
            <Container fluid>
              {/* Left: Logo */}
              <Navbar.Brand as={Link}>
                <Link
                  to="/uaichome"
                  className="nav-link text-dark"
                  tabIndex={-1}
                >
                  <img
                    src={require("../../assets/img/logo.svg")}
                    alt="United Logo"
                    className="img-fluid"
                    id="logo"
                    style={{
                      height: "60px",
                      cursor: "pointer",
                      marginBottom: "-12px",
                      marginTop: "-12px",
                      outline: "none",
                    }}
                    tabIndex={-1}
                  />
                </Link>
              </Navbar.Brand>

              {/* Toggle for mobile view */}
              <Navbar.Toggle aria-controls="navbarScroll" />

              {/* Right: All nav links and search */}
              <Navbar.Collapse
                id="navbarScroll"
                className="justify-content-end"
              >
                <Nav className="ms-auto my-2 my-lg-0" navbarScroll>
                  <Nav.Link
                    as={Link}
                    to="/uaichome/aboutus"
                    active={pathname === "/uaichome/aboutus"}
                  >
                    About Us
                  </Nav.Link>

                  {/* UNCOMMENT is to open Policyholderportal */}
                  <Nav.Link
                    as={Link}
                    to="/uaichome/policyholder"
                    active={pathname === "/uaichome/policyholder"}
                  >
                    Policy Holder
                  </Nav.Link>
                  {/* <Nav.Link
                    onClick={() => {
                      setShowModal(true);
                      setActiveTab("CUSTOMER");
                      dispatch({
                        type: "SET_LOGIN_PAGE_TO_DISPLAY",
                        payload: "CUSTOMER",
                      });
                    }}
                  >
                    Policy Holder
                  </Nav.Link> */}
                  <Nav.Link
                    as={Link}
                    to="/uaichome/claims"
                    active={pathname === "/uaichome/claims"}
                  >
                    Claims Portal
                  </Nav.Link>

                  <Nav.Link
                    as={Link}
                    to="/uaichome/FindAnAgent"
                    active={pathname === "/uaichome/FindAnAgent"}
                  >
                    Become an Agent
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/uaichome/insurance"
                    active={pathname === "/uaichome/insurance"}
                  >
                    Insurance
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/uaichome/leinholder"
                    active={pathname === "/uaichome/leinholder"}
                  >
                    Lien Holder
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/uaichome/careers"
                    active={pathname === "/uaichome/careers"}
                  >
                    Careers
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/uaichome/claims/faq"
                    active={pathname === "/uaichome/claims/faq"}
                  >
                    FAQ's
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/uaichome/contact"
                    active={pathname === "/uaichome/contact"}
                  >
                    Contact
                  </Nav.Link>
                </Nav>
                {/* <Form className="d-flex ms-3"> */}
                <button
                  className="btn btn-primary-login ms-3"
                  onClick={() => setShowModal(true)}
                >
                  Login
                </button>
                {/* </Form> */}
              </Navbar.Collapse>
            </Container>
          </Navbar>
        </>
      )}

      {showModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div
            className={`modal show fade d-block ${
              isForgotPasswordCustomer ? "modal-blur" : ""
            }`}
            id="login-popup-menu-container"
            tabIndex="-1"
            role="dialog"
          >
            <div
              className="modal-dialog modal-xl modal-dialog-centered"
              role="document"
            >
              <div
                className={`modal-content d-flex ${
                  responsiveStatus ? "flex-column" : "flex-row"
                }`}
                style={
                  responsiveStatus
                    ? {
                        width: "auto",
                        maxHeight: "100vh",
                        overflowY: "auto",
                        position: "relative",
                        maxWidth: "450px",
                      }
                    : styles.container || {} // fallback to your desktop style
                }
              >
                {/* Left Panel / Header */}
                <div
                  className={`${
                    responsiveStatus
                      ? ""
                      : "text-white p-4 d-flex flex-column align-items-center justify-content-center"
                  }`}
                  style={
                    responsiveStatus
                      ? {
                          position: "relative",
                          width: "100%",
                          height: "150px",
                        }
                      : { width: "40%" }
                  }
                >
                  {responsiveStatus ? (
                    <>
                      <img
                        src={require("../../assets/img/responsive_icon/popup_layout1.svg")}
                        alt="Background"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <img
                        src={require("../../assets/img/responsive_icon/circles.svg")}
                        alt="Overlay"
                        style={{
                          position: "absolute",
                          top: "10px",
                          left: "10px",
                          width: "100%",
                          height: "50px",
                        }}
                      />
                    </>
                  ) : null}
                </div>

                {/* Right Panel */}
                <div
                  className="p-4 position-relative"
                  style={{ width: responsiveStatus ? "100%" : "60%" }}
                >
                  {/* Close Button */}
                  <button
                    type="button"
                    className="btn-close position-absolute end-0 top-0 m-3"
                    onClick={() => {
                      setShowModal(false);
                      setIsConfirmLoginMessage(false);
                      dispatch({
                        type: "SET_LOGIN_PAGE_TO_DISPLAY",
                        payload: "AGENT",
                      });
                    }}
                    aria-label="Close"
                    style={{ cursor: "pointer", zIndex: 1000 }}
                    tabIndex={-1}
                  ></button>

                  {/* Mobile Welcome Section */}
                  {responsiveStatus && (
                    <div style={styles.welcomeDiv}>
                      <div className="d-block flex-column text-center">
                        <img
                          src={require("../../assets/img/responsive_icon/loginMbanner.svg")}
                          alt="Banner"
                          style={{
                            width: "81%",
                            height: "80%",
                            objectFit: "contain",
                          }}
                        />
                        <img
                          className="mt-2 mb-2"
                          src={require("../../assets/img/logo.svg")}
                          alt="Logo"
                          tabIndex={-1}
                        />
                      </div>
                    </div>
                  )}

                  {/* Desktop Logo */}
                  {!responsiveStatus && (
                    <div className="text-center mb-3">
                      <img
                        src={require("../../assets/img/logo.svg")}
                        alt="United Logo"
                        style={{ height: "50px", outline: "none" }}
                        tabIndex={-1}
                      />
                    </div>
                  )}

                  {/* Tabs & Form Section */}
                  <div
                    className="px-4 position-relative"
                    style={{
                      paddingBottom: responsiveStatus ? "1em" : "",
                      display: responsiveStatus ? "block" : "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Tabs */}
                    <div
                      style={{
                        display: !showTexasAgentMessageReducer
                          ? "flex"
                          : "none",
                        border: "1px solid #ddd",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        width: "100%",
                        overflow: "hidden",
                        padding: "4px",
                        marginBottom: "10px",
                        marginTop: responsiveStatus ? "1.25em" : "",
                      }}
                    >
                      <div
                        onClick={() => {
                          setActiveTab("AGENT");
                          setLoginDisplay("AGENT");
                          setIsCustomer(false);
                          dispatch({
                            type: "SET_LOGIN_PAGE_TO_DISPLAY",
                            payload: "AGENT",
                          });
                        }}
                        style={toggleStyle("AGENT")}
                      >
                        Agent Login
                      </div>
                      <div
                        onClick={() => {
                          setActiveTab("CUSTOMER");
                          setLoginDisplay("CUSTOMER");
                          setIsCustomer(true);
                          dispatch({
                            type: "SET_LOGIN_PAGE_TO_DISPLAY",
                            payload: "CUSTOMER",
                          });
                        }}
                        style={toggleStyle("CUSTOMER")}
                      >
                        Customer Login
                      </div>
                    </div>

                    {/* Login Forms */}
                    {activeTab === "AGENT" && <AgentLogin />}
                    {activeTab === "CUSTOMER" && (
                      <CustomerLogin display="popup" />
                    )}
                    {showTexasAgentMessageReducer && confirmLoginMessage()}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal backdrop */}
          </div>
        </>
      )}

      {responsiveStatus && (
        <>
          <Navbar bg="white" expand="lg" className="shadow-sm px-1 py-3">
            <div className="d-flex justify-content-between align-items-center w-100">
              {/* Left: Toggler Icon */}
              <div>
                <Button variant="" onClick={handleShow} className="border-0">
                  <span className="navbar-toggler-icon" />
                </Button>
              </div>

              {/* Center: Logo Image */}
              <div className="mx-auto position-absolute start-50 translate-middle-x">
                <Navbar.Brand href="#">
                  <img
                    src={require("../../assets/img/logo.svg")}
                    alt="United Logo"
                    className="img-fluid"
                    style={{
                      height: "40px",
                      cursor: "pointer",
                      outline: "none",
                    }}
                    tabIndex={-1}
                  />
                </Navbar.Brand>
              </div>

              {/* Right: Empty space for balancing */}
              <div style={{ width: "auto" }}>
                <button
                  className="btn btn-primary-login"
                  onClick={() => {
                    handleClose();
                    setShowModal(true);
                  }}
                  // disabled
                >
                  Login
                </button>
              </div>
              {/* Width same as toggle icon to balance layout */}
            </div>
          </Navbar>

          {show && (
            <>
              <Offcanvas
                show={show}
                onHide={handleClose}
                responsive="lg"
                placement="start"
              >
                <Offcanvas.Header className="d-flex justify-content-between align-items-center">
                  <Offcanvas.Title>Menu</Offcanvas.Title>
                  <div>
                    <button
                      className="btn btn-primary-login mr-4"
                      onClick={() => {
                        handleClose();
                        setShowModal(true);
                      }}
                    >
                      Login
                    </button>
                    <img
                      src={require("../../assets/img/responsive_icon/ham-menu-close.svg")}
                      alt="Close"
                      className="img-fluid"
                      style={{ height: "40px", cursor: "pointer" }}
                      onClick={handleClose}
                      tabIndex={-1}
                    />
                  </div>
                </Offcanvas.Header>

                <Offcanvas.Body className="d-flex flex-column p-3">
                  <div className="flex-grow-1">
                    <ul className="list-unstyled">
                      {menuItems.map((item, index) => (
                        <li
                          key={index}
                          className="d-flex align-items-center mb-3"
                          onClick={handleClose}
                        >
                          <span className="me-2 fs-5">
                            <img
                              src={require("../../assets/img/responsive_icon/" +
                                item.icon)}
                              alt="Close"
                              className="img-fluid"
                              style={{ height: "40px", cursor: "pointer" }}
                              onClick={handleClose}
                              tabIndex={-1}
                            />
                          </span>
                          {item.label !== "Policy Holder" && (
                            <Nav.Link
                              as={Link}
                              to={item.navigateUrl}
                              active={pathname === item.navigateUrl}
                              onClick={handleClose}
                            >
                              {item.label}
                            </Nav.Link>
                          )}
                          {item.label == "Policy Holder" && (
                            <Nav.Link
                              as={Link}
                              to={item.navigateUrl}
                              active={pathname === item.navigateUrl}
                              // onClick={handleClose}
                              // onClick={() => {
                              //   setShowModal(true);
                              //   setActiveTab("CUSTOMER");
                              //   dispatch({
                              //     type: "SET_LOGIN_PAGE_TO_DISPLAY",
                              //     payload: "CUSTOMER",
                              //   });
                              // }}
                            >
                              Policy Holder
                            </Nav.Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {/* <Button className="w-100 mt-auto" variant="primary">Login</Button> */}
                  {/* <button
                className="btn btn-primary-login ms-3"
                onClick={() => {
                  handleClose();
                  setShowModal(true)}
                }
              >
                Login
              </button> */}
                </Offcanvas.Body>
              </Offcanvas>
            </>
          )}
        </>
      )}

      {showModal && errorMessageComponent()}
      {showModal && messageComponent()}
    </>
  );
};

const styles = {
  container: {
    display: "flex",
    flexWrap: "wrap",
    width: "100%",
    fontFamily: "Arial, sans-serif",
    backgroundImage: `url(${layout})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center",
    height: "auto",
    maxHeight: "75vh",
    borderRadius: "15px",
  },
  container1: {
    display: "flex",
    flexWrap: "wrap",
    width: "100%",
    fontFamily: "Arial, sans-serif",
    backgroundImage: `url(${layout})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center",
    height: "100vh",
    maxHeight: "100vh",
    borderRadius: "15px",
  },
  welcomeDiv: {
    display: "flex",
    flexWrap: "wrap",
    width: "77%",
    fontFamily: "Arial, sans-serif",
    backgroundSize: "cover",
    maxHeight: "100vh",
    borderRadius: "15px",
    zIndex: 0,
    margin: "auto",
    top: "-45",
    marginTop: "-135px",
    textAlign: "center",
  },
};

export default Header;
